import {
  Component,
  EventEmitter,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { noHtmlTagValidator } from '~/core/validators/no-html-tag/no-html-tag.validator';
import { Subscription } from 'rxjs';

import { appConfig } from '~/app.config';
import { promoCodeValidator } from '~/core/validators/promocode/promocode.validator';

import { LoaderService } from '~/core/services/loader/loader.service';

import { PromoCodeService } from '~/payment/services/promocode/promocode.service';

import { VtmnPlayButtonVariantEnum } from '~/component/buttons/vtmn-play-button/enum/vtmn-play-button-variant.enum';
import { ToggleNameEnum } from '~/toggles/services/toggle/enums/name.enum';
import { ModalComponent } from '~/component/modal/modal.component';
import { APP_STORAGE } from '~/app.token';
import { CtaButtonTypeEnum } from '~/component/call-to-action/enums/button-type.enum';
import { VtmnPlayIconTypeEnum } from '~/component/vtmn-play-icon/enums/vtmn-play-icon-type.enum';
import { VtmnPlayButtonShapeEnum } from '~/component/buttons/vtmn-play-button/enum/vtmn-play-button-shape.enum';

@Component({
  selector: 'app-checkout-payment-promo-code',
  templateUrl: './payment-promo-code.component.html',
  styleUrls: ['./payment-promo-code.component.scss'],
})
export class CheckoutPaymentPromoCodeComponent implements OnInit, OnDestroy {
  @Output() public submitPromoCode = new EventEmitter<void>();
  @Output() public readonly focusInput = new EventEmitter<void>();

  @Input() public codeStatus: {};

  private readonly subscriptions = new Subscription();

  public readonly iconType = VtmnPlayIconTypeEnum;
  public readonly toggleNameEnum = ToggleNameEnum;
  public readonly ctaButtonType = CtaButtonTypeEnum;
  public readonly vtmnPlayButtonVariant = VtmnPlayButtonVariantEnum;
  public readonly vtmnPlayButtonShape = VtmnPlayButtonShapeEnum;
  public readonly paymentPromoCodeMinimumLength =
    appConfig.formValidationRules.paymentPromoCodeMinimumLength;
  public readonly paymentPromoCodeMaximumLength =
    appConfig.formValidationRules.paymentPromoCodeMaximumLength;
  @ViewChild('confirmCodeDeletionModal')
  public confirmCodeDeletionModal: ModalComponent;
  public formGroup: FormGroup;
  public promoCode: FormControl;
  public isFormValid: boolean;
  public isLoading = false;
  public promoCodeIsDefined = false;
  public defaultValue = '';

  constructor(
    @Inject(APP_STORAGE) private readonly storage: Storage,
    private readonly promoCodeService: PromoCodeService,
    private readonly loaderService: LoaderService
  ) {}

  public ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  public ngOnInit(): void {
    this.subscriptions.add(
      this.loaderService.loading$.subscribe((loader) => {
        this.isLoading = loader.state.loading;
      })
    );

    this.subscriptions.add(
      this.promoCodeService.getClearInputValue().subscribe((value) => {
        if (value) {
          this.formGroup.controls.promoCode.setValue(null);
          this.promoCodeService.resetClearInputValue();
        }
      })
    );

    const previousCode = JSON.parse(this.promoCodeService.loadPromoCode());

    if (previousCode?.code) {
      this.defaultValue = previousCode.code;
    }

    this.formGroup = new FormGroup({
      promoCode: new FormControl(this.defaultValue, [
        promoCodeValidator(),
        Validators.maxLength(this.paymentPromoCodeMaximumLength),
        noHtmlTagValidator(),
      ]),
    });

    this.subscriptions.add(
      this.formGroup.valueChanges.subscribe((value: string) => {
        this.isFormValid = this.formGroup.valid && this.formGroup.dirty;
      })
    );

    this.subscriptions.add(
      this.promoCodeService.getPromoCodeValue().subscribe((promoCodeValue) => {
        this.promoCodeIsDefined = promoCodeValue !== null;
      })
    );
  }

  public get isButtonDisabled(): boolean {
    return this.formGroup.invalid || this.formGroup.value.promoCode === '';
  }

  public get promoCodeError(): {} {
    return this.codeStatus;
  }

  public submit(): void {
    if (!this.promoCodeIsDefined) {
      this.submitPromoCode.emit(this.formGroup.value);
    }
  }

  public showConfirmCodeDeletionModal(): void {
    this.confirmCodeDeletionModal.show();
  }

  public closeConfirmCodeDeletionModal(): void {
    this.confirmCodeDeletionModal.hide();
  }

  public deletePromoCode() {
    this.closeConfirmCodeDeletionModal();
    this.formGroup.controls.promoCode.setValue(null);
    this.submitPromoCode.emit(null);
    this.promoCodeService.clearPromoCode();
  }
}
